import {getZodFlatSchemaFields} from '@cohort/shared/utils/zod';
import storageAvailable from '@cohort/shared-frontend/utils/storageAvailable';
import {useAppStore} from '@cohort/wallet/hooks/stores/app';
import {useUserStore} from '@cohort/wallet/hooks/stores/user';
import {useApi} from '@cohort/wallet/hooks/useApi';
import {loginUser} from '@cohort/wallet/lib/Authentication';
import {loginWithCustomToken, logoutUser} from '@cohort/wallet/lib/Authentication';
import {UserApi} from '@cohort/wallet/lib/Endpoints';
import {setCurrentUser, trackError} from '@cohort/wallet/lib/Tracking';
import {InitParamsWSchema} from '@cohort/wallet-schemas/init';
import {browserLocalPersistence, getAuth, inMemoryPersistence} from 'firebase/auth';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {shallow} from 'zustand/shallow';

export const useAuthentication = (): void => {
  const initialized = useRef(false);
  const initFromSearchParams = useAppStore(store => store.initFromSearchParams);
  const {user, initEmail, login, logout, setIsAuthenticating} = useUserStore(
    store => ({
      user: store.user,
      initEmail: store.initEmail,
      login: store.login,
      logout: store.logout,
      setIsAuthenticating: store.setIsAuthenticating,
    }),
    shallow
  );
  const verifyAuthToken = useApi(UserApi.verifyAuthToken);
  const navigate = useNavigate();
  const [enableAuthStateChange, setEnableAuthStateChange] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const initCustomTokenAuthentication = useCallback(
    async (authToken: string, cohortRedirect?: string) => {
      // We start by logging out the user - isAuthenticating will be set to false afterwards
      await logoutUser();
      setIsAuthenticating(true);
      const firebaseToken = await verifyAuthToken(authToken).catch(() => {
        setIsAuthenticating(false);
        setEnableAuthStateChange(true);
      });

      if (!firebaseToken) {
        return;
      }
      await loginWithCustomToken(firebaseToken);
      setEnableAuthStateChange(true);
      if (cohortRedirect !== undefined) {
        navigate(cohortRedirect, {replace: true});
      }
    },
    [navigate, verifyAuthToken, setIsAuthenticating]
  );

  // Application default setup
  // Parsing searchParams and initializing the authentication
  useEffect(() => {
    if (initialized.current) {
      return;
    }
    const config = initFromSearchParams(searchParams);

    if (config?.authToken) {
      initCustomTokenAuthentication(config.authToken, config.cohortRedirect);
    } else {
      // We expect an authToken in 2 cases, with an integration or when the app is embedded
      // If we don't have one, we log out the user
      if (config?.integration !== undefined || config?.embedded) {
        logoutUser().then(() => setEnableAuthStateChange(true));
      } else {
        setEnableAuthStateChange(true);
      }
    }
    initialized.current = true;
    const paramsWhitelist = getZodFlatSchemaFields(InitParamsWSchema);
    const paramsToDelete: Array<string> = [];

    searchParams.forEach((value, key) => {
      if (key in paramsWhitelist) {
        paramsToDelete.push(key);
      }
    });

    paramsToDelete.forEach(key => searchParams.delete(key));
    setSearchParams(searchParams, {replace: true});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams]);

  // Set auth persistence
  useEffect(() => {
    async function setAuthPersistence(): Promise<void> {
      const auth = getAuth();

      if (storageAvailable()) {
        await auth.setPersistence(browserLocalPersistence);
      } else {
        await auth.setPersistence(inMemoryPersistence);
      }
    }

    setAuthPersistence();
  }, []);

  // Initialize Firebase auth listener
  useEffect(() => {
    if (!enableAuthStateChange) {
      return;
    }
    const auth = getAuth();

    auth.onAuthStateChanged(async firebaseUser => {
      if (firebaseUser) {
        try {
          const user = await loginUser(firebaseUser);

          login(firebaseUser, user);
          setCurrentUser(user);
        } catch (e) {
          trackError(e);
        }
      } else {
        logout();
        return setCurrentUser(undefined);
      }
    });
  }, [login, logout, enableAuthStateChange]);

  // Logout user on different email / brand
  useEffect(() => {
    if (user && initEmail && user.email !== initEmail) {
      logoutUser();
    }
  }, [user, initEmail]);
};
